/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useState, useReducer } from 'react';
import useProcedure from 'procedure/useProcedure';
import { useDispatch, useSelector } from 'react-redux';
import { initialDraftState, reducer } from 'components/draft/draftReducer';
import { useHistory } from 'react-router';
import draftApi from 'api/draft.api';
import { nestedObjectFromFlattenObject } from 'shared/utils/objectUtils';
import { callApi } from 'api/helpers/api.helpers';
import { ConfigResourceProps } from '../../enums/ConfigResourceProps';
import { useConfiguration } from '../configuration/useConfiguration';
import {
  selectAutofillDialogState,
  selectAutofillFields,
  selectAutofillSubmission,
  updateAutofillDialog,
  updateConfirmAutofillDialog,
  updateUnitaryEffectWarningDialog,
  updateAutofillFields,
  updateAutofillSubmission,
  selectConfirmAutofillDialogState,
  selectUnitaryEffectWarningDialogState,
} from './autofill.slice';
import { findSubmissionByApplicationNumber, findSubmissionByPublicationNumber } from './autofill.thunk';
import { postProcess } from '../../../shared/configuration/businessRules';

export default function useAutofill(procedureId) {
  const history = useHistory();

  const procedure = useProcedure(procedureId);

  const { resource: validationSchema } = useConfiguration(
    ConfigResourceProps.SUBMISSION_AUTOFILL_VALIDATION_SCHEMA,
    procedure.id,
  );
  const submissionUiSchema = useConfiguration(ConfigResourceProps.SUBMISSION_AUTOFILL_UI_SCHEMA, procedure.id);

  const dispatch = useDispatch();

  // eslint-disable-next-line no-unused-vars
  const [draftState, dispatchDraftState] = useReducer(reducer, initialDraftState);

  const submission = useSelector(selectAutofillSubmission);
  const autofillFields = useSelector(selectAutofillFields);
  const autofillDialogState = useSelector(selectAutofillDialogState);
  const confirmAutofillDialogState = useSelector(selectConfirmAutofillDialogState);
  const unitaryEffectWarningDialogState = useSelector(selectUnitaryEffectWarningDialogState);

  const updateSubmission = newSubmission => dispatch(updateAutofillSubmission(newSubmission));
  const updateAutofillFieldsState = newAutofillFields => dispatch(updateAutofillFields(newAutofillFields));

  const updateAutofillDialogState = (state) => {
    const { open } = state;
    if (open) {
      updateSubmission({});
    }
    dispatch(updateAutofillDialog(state));
  };

  const updateConfirmAutofillDialogState = state => dispatch(updateConfirmAutofillDialog(state));
  const updateUnitaryEffectWarningDialogState = state => dispatch(updateUnitaryEffectWarningDialog(state));

  const [uiSchema, setUiSchema] = useState(undefined);
  const [isValid, setIsValid] = useState(false);
  const [extraErrors, setExtraErrors] = useState({});

  useEffect(() => {
    if (submissionUiSchema.ready) setUiSchema(submissionUiSchema.resource);
  }, [submissionUiSchema.ready]);

  const getByApplicationNumber = async ({ applicationNumber, protectionSelection }) => {
    dispatch(findSubmissionByApplicationNumber({ applicationNumber, protectionSelection }));
  };

  const getByPublicationNumber = async ({ publicationNumber, protectionSelection }) => {
    dispatch(findSubmissionByPublicationNumber({ publicationNumber, protectionSelection }));
  };

  const onCheckAction = async () => {
    const { applicationNumber, publicationNumber } = submission.basicFilingInfo?.applicationNumberContainer || {};
    const { definitiveProtectionSelection, typeOfProtection } = submission.basicFilingInfo;

    const protectionSelection = typeOfProtection === 'provisional' ? 'A1' : definitiveProtectionSelection;

    if (applicationNumber) {
      await getByApplicationNumber({ applicationNumber, protectionSelection });
      return;
    }

    await getByPublicationNumber({ publicationNumber, protectionSelection });
  };

  const onFillManuallyAction = async () => {
    const newSubmission = getNewSubmission();

    try {
      let sub = {
        ...newSubmission,
        procedure: procedure.id,
        receivingOffice: procedure.receivingOffice,
      };
      sub = await callApi(draftApi.postProcessBusinessRules)(sub, 'event_new_draft_created');
      sub = await callApi(draftApi.create)(sub);

      updateAutofillDialogState({ open: false });
      updateConfirmAutofillDialogState({ open: false });
      updateUnitaryEffectWarningDialogState({ open: false });

      history.push(`/${procedure.id}/draft/${sub.id}/edit`);
    } catch (err) {
      console.log(err);
    }
  };

  const onContinueAction = async () => {
    const newSubmission = getNewSubmission();

    let mappingResult;

    for (const field of autofillFields) {
      if (field.import === 'no') {
        // eslint-disable-next-line no-continue
        continue;
      }

      const mappingFields = field.mapping.replace('submission.', '');
      mappingResult = nestedObjectFromFlattenObject(mappingFields, field.value, newSubmission);
    }

    try {
      let sub = {
        ...mappingResult,
        procedure: procedure.id,
        receivingOffice: procedure.receivingOffice,
        autofill: true,
      };
      sub = await postProcess(sub, 'event_new_draft_created');
      sub = await callApi(draftApi.create)(sub);

      updateConfirmAutofillDialogState({ open: false });
      history.push(`/${procedure.id}/draft/${sub.id}/edit`);
    } catch (err) {
      console.log(err);
    }
  };

  const getNewSubmission = () => {
    const { definitiveProtectionSelection, typeOfProtection } = submission.basicFilingInfo;
    const protectionSelection = typeOfProtection === 'provisional' ? undefined : definitiveProtectionSelection;

    const newSubmission = {
      ...submission,
      basicFilingInfo: {
        ...submission.basicFilingInfo,
        applicationNumberContainer: undefined,
        definitiveProtectionSelection: protectionSelection,
        userReference: submission.basicFilingInfo.userReference,
      },
    };

    return newSubmission;
  };

  const onCheckAnotherAction = () => {
    updateConfirmAutofillDialogState({ open: false });
    updateAutofillDialogState({ open: true });
  };

  return {
    uiSchema,
    validationSchema,
    setUiSchema,
    setIsValid,
    isValid,
    submission,
    procedure,
    updateAutofillFieldsState,
    updateSubmission,
    autofillFields,
    autofillDialogState,
    confirmAutofillDialogState,
    updateAutofillDialogState,
    updateConfirmAutofillDialogState,
    onFillManuallyAction,
    onContinueAction,
    onCheckAction,
    onCheckAnotherAction,
    dispatchDraftState,
    extraErrors,
    setExtraErrors,
    unitaryEffectWarningDialogState,
    updateUnitaryEffectWarningDialogState,
  };
}
